import { action, makeObservable, observable, runInAction } from 'mobx';
import { directoryAxiosClient } from 'Services/Api/client';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import {
	DirectoryGroup,
	DirectoryRole,
	DirectoryUser,
	GetUsersParams,
	UsersPageParams,
	UsersPageResponse
} from 'Services/Api/Directory/Types';
import { Status } from 'app-types';

const USERS_PAGE_SIZE = 200;
const MAX_USER_PAGES = 100;
const FIRST_USERS_PAGE = 1;

class AssignUsersStore {
	status = {
		fetchOrganizations: Status.Idle,
		fetchAssignmentOrganizations: Status.Idle,
		fetchProperties: Status.Idle,
		fetchPropertiesByTenant: Status.Idle,
		fetchRoles: Status.Idle,
		fetchGroups: Status.Idle,
		fetchUsers: Status.Idle,
		fetchMoreUsers: Status.Idle,
		fetchAllUsers: Status.Idle,
		fetchUsersByIds: Status.Idle,
		fetchFilterUsers: Status.Idle
	};

	organizations: DirectoryGroup[] = [];
	// The organizations an assignment can target. Isolated from `organizations`
	// — that list is every tenant, and it backs the tenant picker itself, so
	// narrowing the create flow to one tenant must not shrink the picker that
	// chose it.
	assignmentOrganizations: DirectoryGroup[] = [];
	properties: DirectoryGroup[] = [];
	// Tenant `properties` was loaded for, so callers can tell a warm list from
	// one belonging to a different tenant. Not observable — bookkeeping.
	propertiesTenantId: number | undefined = undefined;
	// Isolated from `properties` so the two pickers never overwrite each other:
	// this one always belongs to one named tenant (the Save Report modal), while
	// `properties` follows whatever the create flow is currently scoped to.
	tenantProperties: DirectoryGroup[] = [];
	roles: DirectoryRole[] = [];
	groups: DirectoryGroup[] = [];
	users: DirectoryUser[] = [];
	usersTotal = 0;
	hasMoreUsers = false;
	usersPropertyIds: number[] = [];
	usersNextPage = FIRST_USERS_PAGE;
	usersLoadPromise: Promise<void> | null = null;
	allUsersPromise: Promise<DirectoryUser[] | undefined> | null = null;
	// Isolated from `users` so the create-flow property picker is never affected.
	assignmentUsers: DirectoryUser[] = [];
	filterUsers: DirectoryUser[] = [];
	usersRequestId = 0;
	assignmentUsersRequestId = 0;
	filterUsersRequestId = 0;

	constructor() {
		makeObservable(this, {
			status: observable,
			organizations: observable,
			assignmentOrganizations: observable,
			properties: observable,
			tenantProperties: observable,
			roles: observable,
			groups: observable,
			users: observable,
			usersTotal: observable,
			hasMoreUsers: observable,
			assignmentUsers: observable,
			filterUsers: observable
		});
	}

	@action
	async fetchOrganizations(): Promise<void> {
		runInAction(() => {
			this.status.fetchOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			runInAction(() => {
				this.organizations = data;
				this.status.fetchOrganizations = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	/**
	 * The organizations a new assignment can target. With `tenantId` the list is
	 * that one tenant's organization; without it the caller's own token decides
	 * what comes back.
	 */
	@action
	async fetchAssignmentOrganizations(tenantId?: number): Promise<void> {
		runInAction(() => {
			this.status.fetchAssignmentOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				tenantId
					? `${DIRECTORY_API.GROUPS}?id=${tenantId}&label[]=Organization`
					: `${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			runInAction(() => {
				this.assignmentOrganizations = data;
				this.status.fetchAssignmentOrganizations = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchAssignmentOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	/**
	 * `tenantId` narrows the list to that tenant's properties, walking down its
	 * group tree the same way `fetchPropertiesByTenant` does.
	 */
	@action
	async fetchProperties(tenantId?: number): Promise<void> {
		runInAction(() => {
			this.status.fetchProperties = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				tenantId
					? `${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true&label[]=Property`
					: `${DIRECTORY_API.GROUPS}?label[]=Property`
			);
			runInAction(() => {
				this.properties = data;
				this.propertiesTenantId = tenantId;
				this.status.fetchProperties = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchProperties = Status.Failure;
			});
			throw e;
		}
	}

	// One tenant's properties, addressed the way fetchGroupsByProperty walks
	// down from its parent group.
	@action
	async fetchPropertiesByTenant(tenantId: number): Promise<void> {
		if (!tenantId) {
			runInAction(() => {
				this.tenantProperties = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchPropertiesByTenant = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true&label[]=Property`
			);
			runInAction(() => {
				this.tenantProperties = data;
				this.status.fetchPropertiesByTenant = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchPropertiesByTenant = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchRoles(): Promise<void> {
		runInAction(() => {
			this.status.fetchRoles = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryRole[]>(
				DIRECTORY_API.ROLES
			);
			runInAction(() => {
				this.roles = data;
				this.status.fetchRoles = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchRoles = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchGroupsByProperty(propertyIds: number[]): Promise<void> {
		if (!propertyIds.length) {
			runInAction(() => {
				this.groups = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchGroups = Status.Pending;
		});
		try {
			const ids = propertyIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?id=${ids}&include_group_tree=true&label[]=Group`
			);
			runInAction(() => {
				this.groups = data;
				this.status.fetchGroups = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchGroups = Status.Failure;
			});
			throw e;
		}
	}

	private async fetchUsersPage(
		params: GetUsersParams,
		page: number
	): Promise<{ users: DirectoryUser[]; total?: number }> {
		const pageParams: GetUsersParams & UsersPageParams = {
			...params,
			page,
			limit: USERS_PAGE_SIZE
		};
		const { data } = await directoryAxiosClient.get<
			UsersPageResponse | DirectoryUser[]
		>(DIRECTORY_API.USERS, { params: pageParams });

		// Paginating this endpoint moved it onto the envelope the rest of
		// the API uses; the other directory lists still answer with a plain
		// array, so both are read here.
		return {
			users: Array.isArray(data) ? data : data?.data ?? [],
			total: Array.isArray(data) ? undefined : data?.meta?.total
		};
	}

	private async fetchUserPages(
		params: GetUsersParams
	): Promise<DirectoryUser[]> {
		const users: DirectoryUser[] = [];

		for (let index = 0; index < MAX_USER_PAGES; index += 1) {
			const { users: page, total } = await this.fetchUsersPage(
				params,
				FIRST_USERS_PAGE + index
			);

			users.push(...page);
			if (page.length !== USERS_PAGE_SIZE) break;
			if (total !== undefined && users.length >= total) break;
		}

		return users;
	}

	private async loadNextUsersPage(requestId: number): Promise<void> {
		const page = this.usersNextPage;
		const { users, total } = await this.fetchUsersPage(
			{
				group_id: this.usersPropertyIds.join(','),
				include_group_tree: true
			},
			page
		);
		if (requestId !== this.usersRequestId) return;

		const knownIds = new Set(this.users.map((user) => user.id));
		const loaded =
			page === FIRST_USERS_PAGE
				? users
				: [
						...this.users,
						...users.filter((user) => !knownIds.has(user.id))
				  ];
		const hasMore =
			users.length === USERS_PAGE_SIZE &&
			page < FIRST_USERS_PAGE + MAX_USER_PAGES - 1 &&
			(total === undefined || loaded.length < total);

		this.usersNextPage = page + 1;
		runInAction(() => {
			this.users = loaded;
			this.usersTotal = total ?? loaded.length;
			this.hasMoreUsers = hasMore;
		});
	}

	private async loadUsersPageWithStatus(
		requestId: number,
		statusKey: 'fetchUsers' | 'fetchMoreUsers'
	): Promise<void> {
		runInAction(() => {
			this.status[statusKey] = Status.Pending;
		});
		try {
			await this.loadNextUsersPage(requestId);
			if (requestId !== this.usersRequestId) return;
			runInAction(() => {
				this.status[statusKey] = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.usersRequestId) return;
			runInAction(() => {
				this.status[statusKey] = Status.Failure;
			});
			throw e;
		}
	}

	private runUsersLoad(
		requestId: number,
		statusKey: 'fetchUsers' | 'fetchMoreUsers'
	): Promise<void> {
		const load = this.loadUsersPageWithStatus(requestId, statusKey);
		const clear = () => {
			if (this.usersLoadPromise === load) this.usersLoadPromise = null;
		};
		this.usersLoadPromise = load;
		load.then(clear, clear);
		return load;
	}

	@action
	async fetchUsersByProperty(propertyIds: number[]): Promise<void> {
		const requestId = (this.usersRequestId += 1);
		this.usersPropertyIds = propertyIds;
		this.usersNextPage = FIRST_USERS_PAGE;
		this.usersLoadPromise = null;
		this.allUsersPromise = null;

		runInAction(() => {
			this.hasMoreUsers = false;
			this.status.fetchMoreUsers = Status.Idle;
			this.status.fetchAllUsers = Status.Idle;
		});

		if (!propertyIds.length) {
			runInAction(() => {
				this.users = [];
				this.usersTotal = 0;
			});
			return;
		}
		return this.runUsersLoad(requestId, 'fetchUsers');
	}

	@action
	fetchNextUsersPage(): Promise<void> {
		if (this.usersLoadPromise) return this.usersLoadPromise;
		if (!this.hasMoreUsers) return Promise.resolve();
		return this.runUsersLoad(this.usersRequestId, 'fetchMoreUsers');
	}

	private async loadRemainingUsers(
		requestId: number
	): Promise<DirectoryUser[] | undefined> {
		runInAction(() => {
			this.status.fetchAllUsers = Status.Pending;
		});
		try {
			if (this.usersLoadPromise) await this.usersLoadPromise;
			while (requestId === this.usersRequestId && this.hasMoreUsers) {
				await this.fetchNextUsersPage();
			}
			if (requestId !== this.usersRequestId) return undefined;
			runInAction(() => {
				this.status.fetchAllUsers = Status.Success;
			});
			return this.users;
		} catch (e) {
			if (requestId !== this.usersRequestId) return undefined;
			runInAction(() => {
				this.status.fetchAllUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	fetchAllUsers(): Promise<DirectoryUser[] | undefined> {
		if (this.allUsersPromise) return this.allUsersPromise;

		const load = this.loadRemainingUsers(this.usersRequestId);
		const clear = () => {
			if (this.allUsersPromise === load) this.allUsersPromise = null;
		};
		this.allUsersPromise = load;
		load.then(clear, clear);
		return load;
	}

	/**
	 * The ids of everyone under the given groups — properties, organizations and
	 * groups are all directory groups, so one query covers them. Used where an
	 * endpoint takes user_ids but the user picked whole groups.
	 */
	@action
	async fetchUserIdsByGroups(groupIds: number[]): Promise<string[]> {
		if (!groupIds.length) return [];
		const users = await this.fetchUserPages({
			group_id: groupIds.join(','),
			include_group_tree: true
		});
		return users.map((user) => user.id);
	}

	@action
	async fetchGroupsByIds(groupIds: number[]): Promise<DirectoryGroup[]> {
		if (!groupIds.length) return [];
		const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
			`${DIRECTORY_API.GROUPS}?id=${groupIds.join(',')}`
		);
		return data;
	}

	@action
	async fetchFilterUsers(): Promise<void> {
		const requestId = (this.filterUsersRequestId += 1);

		runInAction(() => {
			this.status.fetchFilterUsers = Status.Pending;
		});
		try {
			const users = await this.fetchUserPages({});
			if (requestId !== this.filterUsersRequestId) return;
			runInAction(() => {
				this.filterUsers = users;
				this.status.fetchFilterUsers = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.filterUsersRequestId) throw e;
			runInAction(() => {
				this.status.fetchFilterUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchUsersByIds(userIds: string[]): Promise<void> {
		const requestId = (this.assignmentUsersRequestId += 1);

		if (!userIds.length) {
			runInAction(() => {
				this.assignmentUsers = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchUsersByIds = Status.Pending;
		});
		try {
			const users = await this.fetchUserPages({
				id: userIds.join(','),
				include_group_tree: false
			});
			if (requestId !== this.assignmentUsersRequestId) return;
			runInAction(() => {
				this.assignmentUsers = users;
				this.status.fetchUsersByIds = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.assignmentUsersRequestId) throw e;
			runInAction(() => {
				this.status.fetchUsersByIds = Status.Failure;
			});
			throw e;
		}
	}
}

export const assignUsersStore = new AssignUsersStore();
export default AssignUsersStore;
